/**
 * Dashboard Page
 * Main HC Dashboard with real-time metrics from backend API
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Table,
  Tag,
  Space,
  Button,
  Alert,
  Progress,
  List,
  message,
} from 'antd';
import {
  EyeOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { dashboardApi, agentApi, auditApi } from '../api/hcApi';

const { Title, Text } = Typography;

interface DashboardSummary {
  systemStatus: string;
  currentMetrics: {
    autoFixPercent: number;
    mttrMinutes: number;
    incidentsTotal: number;
  };
  weeklyAverages: {
    autoFixPercent: number;
    mttrMinutes: number;
    totalIncidents: number;
    totalAudits: number;
  };
  agents: {
    total: number;
    active: number;
    healthy: number;
    healthRate: number;
  };
  policies: {
    total: number;
    active: number;
    safe: number;
    avgSuccessRate: number;
  };
  activity: {
    auditsLast24h: number;
    pendingSignoffs: number;
  };
  computedAt: string;
}

interface TrendPoint {
  date: string;
  avgAutoFix: number | null;
  avgMttr: number | null;
  totalIncidents: number;
  auditCount: number;
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [trendData, setTrendData] = useState<TrendPoint[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [recentAudits, setRecentAudits] = useState<any[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchDashboardData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [summaryRes, agentsRes, auditsRes] = await Promise.allSettled([
        dashboardApi.getSummary(),
        agentApi.list(),
        auditApi.getRecent(10),
      ]);

      // Process summary
      if (summaryRes.status === 'fulfilled' && summaryRes.value?.success) {
        const summaryData = summaryRes.value.data as unknown as DashboardSummary;
        setSummary(summaryData);
        // WP-HC6 (bug-1466): "Last updated" used to be the browser clock
        // (new Date()), which lies about staleness if the ingestion cron
        // is dead — the badge would always say "just now". Use the
        // server-computed timestamp from the summary payload instead.
        if (summaryData?.computedAt) {
          setLastUpdated(new Date(summaryData.computedAt));
        }
      }

      // Process agents
      if (agentsRes.status === 'fulfilled' && agentsRes.value?.success) {
        setAgents(agentsRes.value.data as any[] || []);
      }

      // Process recent audits
      if (auditsRes.status === 'fulfilled' && auditsRes.value?.success) {
        setRecentAudits(auditsRes.value.data as any[] || []);
      }

      // Build trend from summary weekly data
      try {
        if (summaryRes.status === 'fulfilled' && summaryRes.value?.success) {
          const s = summaryRes.value.data as unknown as DashboardSummary;
          // Generate trend from available data
          const now = new Date();
          const points: TrendPoint[] = [];
          for (let i = 6; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(d.getDate() - i);
            points.push({
              date: d.toLocaleDateString('en-US', { weekday: 'short' }),
              avgAutoFix: s.weeklyAverages.autoFixPercent || 0,
              avgMttr: s.weeklyAverages.mttrMinutes || 0,
              totalIncidents: Math.round((s.weeklyAverages.totalIncidents || 0) / 7),
              auditCount: Math.round((s.weeklyAverages.totalAudits || 0) / 7),
            });
          }
          setTrendData(points);
        }
      } catch (trendErr) {
        console.warn('Trend data fetch failed:', trendErr);
      }
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchDashboardData(), 60000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    fetchDashboardData(true);
    message.info('Refreshing dashboard...');
  };

  if (loading) {
    return <LoadingSpinner tip="Loading dashboard..." />;
  }

  const systemStatus = summary?.systemStatus || 'UNKNOWN';
  const statusColor = systemStatus === 'GREEN' ? '#52c41a' : systemStatus === 'YELLOW' ? '#faad14' : '#f5222d';

  // WP-HC6 (bug-1466): staleness is derived from the server-time
  // `computedAt` set above, not the browser clock. 90 min = 3x the
  // 30-min ingestion cron cadence.
  const ageMinutes = Math.round((Date.now() - lastUpdated.getTime()) / 60000);
  const isStale = ageMinutes > 90;

  const agentStatusColors: Record<string, string> = {
    active: 'success',
    healthy: 'success',
    idle: 'warning',
    busy: 'processing',
    down: 'error',
    maintenance: 'default',
    degraded: 'warning',
    unhealthy: 'error',
  };

  return (
    <div>
      {/* Page Header */}
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Dashboard Overview
          </Title>
          <Text type="secondary">
            Real-time health monitoring and automated healing status
          </Text>
        </div>
        <Space>
          <Tag color={statusColor} style={{ fontSize: 14, padding: '4px 12px' }}>
            System: {systemStatus}
          </Tag>
          <Text type={isStale ? 'danger' : 'secondary'}>
            Data as of {lastUpdated.toLocaleString()} ({ageMinutes} min ago{isStale ? ' — STALE' : ''})
          </Text>
          <Button icon={<ReloadOutlined spin={refreshing} />} onClick={handleRefresh} loading={refreshing}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Data Loading Issue"
          description={`Some dashboard data could not be loaded: ${error}. Showing available data.`}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* System Status Alert */}
      {systemStatus === 'YELLOW' && (
        <Alert
          message="System Degraded"
          description="Some services are experiencing issues. HC-Healer is actively working on resolution."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          closable
          style={{ marginBottom: 24 }}
        />
      )}
      {systemStatus === 'RED' && (
        <Alert
          message="System Critical"
          description="Critical issues detected. HC-Healer and HC-Debugger are investigating."
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Top Metrics Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Weekly Incidents"
            value={summary?.weeklyAverages.totalIncidents || 0}
            icon={<EyeOutlined />}
            color="#1890ff"
            trend={{
              value: summary?.activity.auditsLast24h || 0,
              direction: 'up',
              type: 'neutral',
            }}
            tooltip="Total incidents recorded in the last 7 days"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Active Policies"
            value={summary?.policies.active || 0}
            icon={<SafetyCertificateOutlined />}
            color="#52c41a"
            trend={{
              value: summary?.policies.safe || 0,
              direction: 'up',
              type: 'positive',
            }}
            tooltip={`${summary?.policies.total || 0} total policies, ${summary?.policies.safe || 0} marked safe`}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Auto-Fix Rate"
            value={summary?.currentMetrics.autoFixPercent || 0}
            suffix="%"
            precision={1}
            icon={<ThunderboltOutlined />}
            color="#722ed1"
            trend={{
              value: summary?.weeklyAverages.autoFixPercent || 0,
              direction: (summary?.currentMetrics.autoFixPercent || 0) >= (summary?.weeklyAverages.autoFixPercent || 0) ? 'up' : 'down',
              type: 'positive',
            }}
            tooltip="Percentage of incidents auto-fixed by HC-Healer"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="MTTR (min)"
            value={summary?.currentMetrics.mttrMinutes || 0}
            precision={1}
            icon={<ClockCircleOutlined />}
            color="#13c2c2"
            trend={{
              value: summary?.weeklyAverages.mttrMinutes || 0,
              direction: (summary?.currentMetrics.mttrMinutes || 0) <= (summary?.weeklyAverages.mttrMinutes || 0) ? 'up' : 'down',
              type: (summary?.currentMetrics.mttrMinutes || 0) <= (summary?.weeklyAverages.mttrMinutes || 0) ? 'positive' : 'negative',
            }}
            tooltip="Mean Time To Recovery in minutes"
          />
        </Col>
      </Row>

      {/* Main Content Row */}
      <Row gutter={[16, 16]}>
        {/* Trend Chart */}
        <Col xs={24} lg={16}>
          <Card
            title="Weekly Trend"
            extra={<Text type="secondary">Last 7 days</Text>}
          >
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorIncidents" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1890ff" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1890ff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorAudits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#52c41a" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#52c41a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#8c8c8c" />
                  <YAxis stroke="#8c8c8c" />
                  <RechartsTooltip />
                  <Area
                    type="monotone"
                    dataKey="totalIncidents"
                    stroke="#1890ff"
                    fill="url(#colorIncidents)"
                    name="Incidents"
                  />
                  <Area
                    type="monotone"
                    dataKey="auditCount"
                    stroke="#52c41a"
                    fill="url(#colorAudits)"
                    name="Audits"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">No trend data available yet. Data will appear after the first audit cycle.</Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Agent Status */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <span>Agent Status</span>
                <Tag color={summary?.agents.healthy === summary?.agents.total ? 'success' : 'warning'}>
                  {summary?.agents.healthy || 0}/{summary?.agents.total || 0} healthy
                </Tag>
              </Space>
            }
            extra={<RobotOutlined />}
          >
            {agents.length > 0 ? (
              <List
                dataSource={agents}
                renderItem={(agent: any) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{agent.agent_id || agent.name || agent.id}</Text>
                          <Tag color={agentStatusColors[agent.status || agent.health] || 'default'}>
                            {(agent.status || agent.health || 'unknown').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Last heartbeat:{' '}
                            {agent.last_heartbeat
                              ? new Date(agent.last_heartbeat).toLocaleTimeString()
                              : 'N/A'}
                          </Text>
                          {agent.health_rate !== undefined && (
                            <Progress
                              percent={agent.health_rate}
                              size="small"
                              status="active"
                              style={{ width: 120 }}
                            />
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <Text type="secondary">
                  No agent data yet. Agents will report after their first health cycle.
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Bottom Row */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* Summary Stats */}
        <Col xs={24} lg={12}>
          <Card title="System Summary">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Agents</Text>
                  <Progress
                    type="circle"
                    percent={summary?.agents.healthRate || 0}
                    size={80}
                    format={() => `${summary?.agents.healthy || 0}/${summary?.agents.total || 0}`}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Policy Success</Text>
                  <Progress
                    type="circle"
                    percent={summary?.policies.avgSuccessRate || 0}
                    size={80}
                    strokeColor={
                      (summary?.policies.avgSuccessRate || 0) >= 90 ? '#52c41a' :
                      (summary?.policies.avgSuccessRate || 0) >= 70 ? '#faad14' : '#f5222d'
                    }
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center', padding: 8 }}>
                  <Text type="secondary">Audits (24h)</Text>
                  <Title level={3} style={{ margin: '4px 0' }}>{summary?.activity.auditsLast24h || 0}</Title>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center', padding: 8 }}>
                  <Text type="secondary">Pending Signoffs</Text>
                  <Title level={3} style={{ margin: '4px 0', color: (summary?.activity.pendingSignoffs || 0) > 0 ? '#faad14' : undefined }}>
                    {summary?.activity.pendingSignoffs || 0}
                  </Title>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Recent Audits */}
        <Col xs={24} lg={12}>
          <Card
            title="Recent Healing Actions"
            extra={
              <Button type="link" size="small" href="#/audits">
                View All
              </Button>
            }
          >
            {recentAudits.length > 0 ? (
              <Table
                dataSource={recentAudits.slice(0, 5)}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: 'Status',
                    dataIndex: 'system_status',
                    width: 90,
                    render: (status: string) => {
                      const colors: Record<string, string> = {
                        GREEN: 'success',
                        YELLOW: 'warning',
                        RED: 'error',
                        completed: 'success',
                        failed: 'error',
                        pending: 'processing',
                      };
                      return <Tag color={colors[status] || 'default'}>{status}</Tag>;
                    },
                  },
                  {
                    title: 'Type',
                    dataIndex: 'audit_type',
                    ellipsis: true,
                  },
                  {
                    title: 'Auto-Fix',
                    dataIndex: 'auto_fixed_percent',
                    width: 90,
                    render: (val: number) => val != null ? `${val}%` : '-',
                  },
                  {
                    title: 'Date',
                    dataIndex: 'audit_date',
                    width: 100,
                    render: (ts: string) => (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {ts ? new Date(ts).toLocaleDateString() : '-'}
                      </Text>
                    ),
                  },
                ]}
              />
            ) : (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <Text type="secondary">
                  No audit data yet. Audits will appear after the first HC cycle completes.
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Auto-Fix Rate Chart */}
      {trendData.length > 0 && trendData.some(d => d.avgAutoFix !== null) && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="Auto-Fix Rate Trend">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#8c8c8c" />
                  <YAxis domain={[0, 100]} stroke="#8c8c8c" />
                  <RechartsTooltip />
                  <Line
                    type="monotone"
                    dataKey="avgAutoFix"
                    stroke="#52c41a"
                    strokeWidth={2}
                    dot={{ fill: '#52c41a', strokeWidth: 2 }}
                    name="Auto-Fix Rate %"
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Dashboard;
